import { State, Action, StateContext } from '@ngxs/store';
import {
  ApiGetAllCustomerServiceOfferingsAction,
  ApiGetAllCustomerServiceOfferingsErrorAction,
  ApiGetAllCustomerServiceOfferingsSuccessAction,
  ApiGetAssignedCustomerServiceProductsAction,
  ApiGetAssignedCustomerServiceProductsErrorAction,
  ApiGetAssignedCustomerServiceProductsSuccessAction,
  ApiGetAvailableCustomerServiceOfferingsAction,
  ApiGetAvailableCustomerServiceOfferingsErrorAction,
  ApiGetAvailableCustomerServiceOfferingsSuccessAction,
  CustomerServiceProductAddAction,
  CustomerServiceProductRemoveAction,
  CustomerServiceProductResetAction,
} from './customer-service-product.actions';
import { Injectable, inject } from '@angular/core';
import { append, patch, removeItem } from '@ngxs/store/operators';
import {
  CustomerServiceOffering,
  CustomerServiceOfferingResponse,
} from '@sales/modules/details/models/customer-service-offering.model';
import { CustomerService } from '@sales/modules/details/services/customer.service';
import { catchError, tap } from 'rxjs/operators';
import { NotificationService } from '@core/services/notification.service';
import { CustomerServiceProduct } from '@shared/models/customer-service-product';
import { StateHelperService } from '@core/services/state-helper.service';
import { ProductStartIndex } from '@shared/models/product.model';
import { AssignedCustomerServiceModel } from '@app/sales/modules/details/models/assigned-customer-services.model';

export interface CustomerServiceProductsStateModel {
  assignedProducts: AssignedCustomerServiceModel[];
  addedProducts: CustomerServiceProduct[];
  isLoadingAssignedCustomerServiceProducts: boolean;
  availableCustomerServiceOfferingsForCustomer: CustomerServiceOffering[];
  isLoadingAllCustomerServiceOfferings: boolean;
  hasLoadedAllCustomerServiceOfferings: boolean;
  allCustomerServiceOfferings: CustomerServiceOffering[];
  isLoadingAvailableCustomerServiceOfferings: boolean;
  hasLoadedAvailableCustomerServiceOfferings: boolean;
}

const initialCustomerServiceProductsState: CustomerServiceProductsStateModel = {
  assignedProducts: [],
  addedProducts: [],
  isLoadingAssignedCustomerServiceProducts: false,
  allCustomerServiceOfferings: [],
  isLoadingAllCustomerServiceOfferings: false,
  hasLoadedAllCustomerServiceOfferings: false,
  availableCustomerServiceOfferingsForCustomer: [],
  isLoadingAvailableCustomerServiceOfferings: false,
  hasLoadedAvailableCustomerServiceOfferings: false,
};

enum Messages {
  GetCustomerServiceOfferingFailed = 'Feil ved henting av tilgjengelige kundetjenester',
}

@State<CustomerServiceProductsStateModel>({
  name: 'customerServiceProducts',
  defaults: initialCustomerServiceProductsState,
})
@Injectable()
export class CustomerServiceProductState {
  private customerService = inject(CustomerService);
  private notificationService = inject(NotificationService);
  private stateHelper = inject(StateHelperService);

  @Action(CustomerServiceProductAddAction)
  public addCustomerServiceProduct(
    { setState, getState }: StateContext<CustomerServiceProductsStateModel>,
    { offering }: CustomerServiceProductAddAction
  ) {
    var product = new CustomerServiceProduct();
    product.customerServiceOffering = offering;
    product.id = this.stateHelper.getIncrementedProductId(
      getState().addedProducts,
      ProductStartIndex.CustomerServiceProduct
    );

    setState(patch({ addedProducts: append<CustomerServiceProduct>([product]) }));
  }

  @Action(CustomerServiceProductRemoveAction)
  public removeCustomerServiceProduct(
    { setState }: StateContext<CustomerServiceProductsStateModel>,
    { productId }: CustomerServiceProductRemoveAction
  ) {
    setState(
      patch({
        addedProducts: removeItem<CustomerServiceProduct>(i => i.id === productId),
      })
    );
  }

  @Action(ApiGetAvailableCustomerServiceOfferingsAction)
  public getAvailableCustomerServiceOfferings(
    { patchState, dispatch, getState }: StateContext<CustomerServiceProductsStateModel>,
    { customerId }: ApiGetAvailableCustomerServiceOfferingsAction
  ) {
    if (getState().hasLoadedAvailableCustomerServiceOfferings) return;

    patchState({ isLoadingAvailableCustomerServiceOfferings: true });
    return this.customerService.getAvailableCustomerServiceOfferings(customerId).pipe(
      tap((response: CustomerServiceOfferingResponse) =>
        dispatch(new ApiGetAvailableCustomerServiceOfferingsSuccessAction(response))
      ),
      catchError(() => dispatch(new ApiGetAvailableCustomerServiceOfferingsErrorAction()))
    );
  }

  @Action(ApiGetAvailableCustomerServiceOfferingsSuccessAction)
  public getAvailableCustomerServiceOfferingsSuccess(
    { patchState }: StateContext<CustomerServiceProductsStateModel>,
    { customerOfferingServiceResponse }: ApiGetAvailableCustomerServiceOfferingsSuccessAction
  ) {
    patchState({
      availableCustomerServiceOfferingsForCustomer: customerOfferingServiceResponse.results,
      isLoadingAvailableCustomerServiceOfferings: false,
      hasLoadedAvailableCustomerServiceOfferings: true,
    });
  }

  @Action(ApiGetAvailableCustomerServiceOfferingsErrorAction)
  public ApiGetAvailableCustomerServiceOfferingsErrorAction({
    patchState,
  }: StateContext<CustomerServiceProductsStateModel>) {
    this.notificationService.error(Messages.GetCustomerServiceOfferingFailed);
    patchState({
      isLoadingAvailableCustomerServiceOfferings: false,
    });
  }

  @Action(ApiGetAllCustomerServiceOfferingsAction)
  public getAllCustomerServiceOfferings({
    patchState,
    dispatch,
    getState,
  }: StateContext<CustomerServiceProductsStateModel>) {
    if (getState().hasLoadedAllCustomerServiceOfferings) return;
    patchState({ isLoadingAllCustomerServiceOfferings: true });
    return this.customerService.getCustomerServiceOfferings().pipe(
      tap((response: CustomerServiceOfferingResponse) =>
        dispatch(new ApiGetAllCustomerServiceOfferingsSuccessAction(response))
      ),
      catchError(() => dispatch(new ApiGetAllCustomerServiceOfferingsErrorAction()))
    );
  }

  @Action(ApiGetAllCustomerServiceOfferingsSuccessAction)
  public getAllCustomerServiceOfferingsSuccess(
    { patchState }: StateContext<CustomerServiceProductsStateModel>,
    { customerOfferingServiceResponse }: ApiGetAllCustomerServiceOfferingsSuccessAction
  ) {
    patchState({
      allCustomerServiceOfferings: customerOfferingServiceResponse.results,
      isLoadingAllCustomerServiceOfferings: false,
      hasLoadedAllCustomerServiceOfferings: true,
    });
  }

  @Action(ApiGetAllCustomerServiceOfferingsErrorAction)
  public getAllCustomerServiceOfferingsError({ patchState }: StateContext<CustomerServiceProductsStateModel>) {
    this.notificationService.error(Messages.GetCustomerServiceOfferingFailed);
    patchState({
      isLoadingAllCustomerServiceOfferings: false,
    });
  }

  @Action(ApiGetAssignedCustomerServiceProductsAction)
  public getAssignedCustomerServiceProducts(
    { patchState, dispatch }: StateContext<CustomerServiceProductsStateModel>,
    { customerId }: ApiGetAssignedCustomerServiceProductsAction
  ) {
    patchState({ isLoadingAssignedCustomerServiceProducts: true });
    return this.customerService.getAssignedCustomerServices(customerId).pipe(
      tap((response: AssignedCustomerServiceModel[]) =>
        dispatch(new ApiGetAssignedCustomerServiceProductsSuccessAction(response))
      ),
      catchError(() => dispatch(new ApiGetAssignedCustomerServiceProductsErrorAction()))
    );
  }

  @Action(ApiGetAssignedCustomerServiceProductsSuccessAction)
  public getAssignedCustomerServiceProductsSuccess(
    { patchState }: StateContext<CustomerServiceProductsStateModel>,
    { assignedCustomerServices }: ApiGetAssignedCustomerServiceProductsSuccessAction
  ) {
    patchState({
      assignedProducts: assignedCustomerServices.map(assignedProduct => {
        return {
          ...assignedProduct,
          monthlyCost: assignedProduct.costs.find(cost => cost.billingPeriodUnit === 'MONTH'),
        };
      }),
      isLoadingAssignedCustomerServiceProducts: false,
    });
  }

  @Action(ApiGetAssignedCustomerServiceProductsErrorAction)
  public getAssignedCustomerServiceProductsError({ patchState }: StateContext<CustomerServiceProductsStateModel>) {
    this.notificationService.error(Messages.GetCustomerServiceOfferingFailed);
    patchState({
      isLoadingAssignedCustomerServiceProducts: false,
    });
  }

  @Action(CustomerServiceProductResetAction)
  public customerServiceProductReset({ patchState }: StateContext<CustomerServiceProductsStateModel>) {
    patchState(initialCustomerServiceProductsState);
  }
}
